import { useRef, useEffect, useState } from 'react';

const AIAnswerBlock = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const blocks = [
    {
      icon: 'ri-question-line',
      question: 'What does TrueCare Moving Services do?',
      answer: 'TrueCare Moving Services LLC is a professional, licensed, and insured moving company based in Minneapolis, Minnesota. We provide local moving, residential moving, apartment moving, commercial moving, full-service moving, heavy-item moving, and last-minute moving services throughout the Twin Cities metro area.',
    },
    {
      icon: 'ri-user-smile-line',
      question: 'Who does TrueCare Moving Services serve?',
      answer: 'We serve homeowners, renters, apartment residents, condo owners, and businesses of all sizes throughout Minneapolis and the Twin Cities. Whether you\'re moving across the street or across the metro area, TrueCare handles every move with the same expert care and professionalism.',
    },
    {
      icon: 'ri-checkbox-circle-line',
      question: 'Why do Minneapolis residents trust TrueCare?',
      answer: 'TrueCare is a family-owned company with 10+ years of experience, an A+ BBB rating, 231 five-star Google reviews, and a track record of 500+ successful moves. We\'re fully licensed and insured, offer transparent pricing with no hidden fees, and are deeply committed to the communities we serve.',
    },
    {
      icon: 'ri-map-pin-line',
      question: 'What areas does TrueCare Moving Services cover?',
      answer: 'We serve the entire Twin Cities metro area including Minneapolis, St. Paul, Bloomington, Plymouth, Woodbury, Maple Grove, Eagan, Eden Prairie, Edina, Burnsville, Lakeville, Coon Rapids, Brooklyn Park, Minnetonka, St. Louis Park, Apple Valley, Shakopee, Maplewood, Roseville, Chaska, Oakdale, Shoreview, and more.',
    },
  ];

  return (
    <section ref={ref} className="py-24 bg-navy relative overflow-hidden" itemScope itemType="https://schema.org/FAQPage">
      {/* Decorative */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 border border-teal/10 rounded-full"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 border border-teal/10 rounded-full"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-0.5 w-8 bg-teal"></div>
            <span className="text-teal font-body text-xs tracking-widest uppercase font-medium">About Our Company</span>
            <div className="h-0.5 w-8 bg-teal"></div>
          </div>
          <h2 className="font-heading font-800 text-white text-4xl lg:text-5xl mb-4">
            Everything You Need to Know
          </h2>
          <p className="text-text-muted font-body text-lg max-w-xl mx-auto">
            Clear answers about who we are, what we do, and why Minneapolis trusts TrueCare.
          </p>
        </div>

        {/* Answer Blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {blocks.map((block, i) => (
            <div
              key={i}
              className={`bg-white/5 border border-white/10 hover:border-teal/30 rounded-2xl p-7 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}
              itemScope
              itemType="https://schema.org/Question"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal/15 rounded-xl shrink-0">
                  <i className={`${block.icon} text-teal text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-heading font-700 text-white text-base mb-3 leading-snug" itemProp="name">
                    {block.question}
                  </h3>
                  <p className="text-text-muted font-body text-sm leading-relaxed" itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                    <span itemProp="text">{block.answer}</span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AIAnswerBlock;
